/**
 * idbConnection.ts — one IndexedDB connection per database, that lets go when
 * asked and never waits forever to open (2026-09-28, Malaika's report: a
 * freeze that survived a hard refresh until every window was closed).
 *
 * What it replaces, read from the code:
 *   - the thread cache (threadPersist.ts) opened a NEW connection for every
 *     read and every write and never closed one. Each open connection holds
 *     the database: a sign-out's deleteDatabase stays pending, and another
 *     tab's upgrade waits, until the page that holds it goes away — which is
 *     "close every window and sign in again";
 *   - the draft store (draftPersistence.ts) kept its first answer forever,
 *     including "no database" after one blocked open, and never let go of the
 *     connection when another tab asked for it.
 *
 * Now each database has one connection per page. It closes itself when
 * another tab or a delete asks for the database (`versionchange`), and is
 * reopened on the next use. An open that has not finished in OPEN_TIMEOUT_MS
 * answers null (the callers already treat null as "no database here" and fall
 * back to localStorage or nothing); a late success is closed at once so it
 * holds nothing. After a failed or late open, the next attempt waits a short,
 * growing backoff, so a stuck database is not hammered, and is never given up
 * on for good. closeAllIdbConnections() lets go of every one (sign-out, before
 * deleting the databases).
 */
import { noteEvent, noteTiming } from './chatHealthCounters';

export const OPEN_TIMEOUT_MS = 3_000;
const BACKOFF_START_MS = 2_000;
const BACKOFF_MAX_MS = 30_000;

export interface IdbConnectionSpec {
  name: string;
  version: number;
  upgrade: (db: IDBDatabase) => void;
  openTimeoutMs?: number;
}

export interface IdbConnection {
  /** The open connection, or null (no IndexedDB, blocked, too slow, failed). Never rejects. */
  get(): Promise<IDBDatabase | null>;
  /** Let go of the connection now (it reopens on the next get). */
  close(): void;
}

const registry = new Set<IdbConnection>();

export function createIdbConnection(spec: IdbConnectionSpec): IdbConnection {
  const timeoutMs = spec.openTimeoutMs ?? OPEN_TIMEOUT_MS;
  let db: IDBDatabase | null = null;
  let opening: Promise<IDBDatabase | null> | null = null;
  let retryAfter = 0;
  let backoff = BACKOFF_START_MS;

  const forget = (which: IDBDatabase): void => {
    if (db === which) db = null;
  };

  const fail = (): null => {
    retryAfter = Date.now() + backoff;
    backoff = Math.min(BACKOFF_MAX_MS, backoff * 2);
    return null;
  };

  const open = (): Promise<IDBDatabase | null> => new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const startedAt = Date.now();
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (value: IDBDatabase | null): void => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      resolve(value);
    };
    let req: IDBOpenDBRequest;
    try {
      req = indexedDB.open(spec.name, spec.version);
    } catch {
      finish(fail());
      return;
    }
    timer = setTimeout(() => {
      // Too slow (another tab holds it, or the browser is stuck): answer null
      // now; whatever the open does later, it is closed and holds nothing.
      noteEvent('idb-blocked');
      finish(fail());
    }, timeoutMs);
    req.onblocked = () => noteEvent('idb-blocked');
    req.onupgradeneeded = () => {
      try { spec.upgrade(req.result); } catch { /* a failed upgrade surfaces as an error below */ }
    };
    req.onsuccess = () => {
      const opened = req.result;
      if (settled) {
        try { opened.close(); } catch { /* already closed */ }
        return;
      }
      noteTiming('idbOpen', Date.now() - startedAt);
      opened.onversionchange = () => {
        // Another tab upgrades, or a sign-out deletes: let go at once.
        try { opened.close(); } catch { /* already closed */ }
        forget(opened);
      };
      opened.onclose = () => forget(opened);
      backoff = BACKOFF_START_MS;
      retryAfter = 0;
      db = opened;
      finish(opened);
    };
    req.onerror = () => {
      noteTiming('idbOpen', Date.now() - startedAt);
      finish(fail());
    };
  });

  const connection: IdbConnection = {
    get(): Promise<IDBDatabase | null> {
      if (db) return Promise.resolve(db);
      if (opening) return opening;
      if (Date.now() < retryAfter) return Promise.resolve(null);
      opening = open().finally(() => { opening = null; });
      return opening;
    },
    close(): void {
      const current = db;
      db = null;
      if (current) {
        try { current.close(); } catch { /* already closed */ }
      }
    },
  };
  registry.add(connection);
  return connection;
}

/** Let go of every connection this page holds (before deleting the databases). */
export function closeAllIdbConnections(): void {
  registry.forEach((connection) => connection.close());
}
